import { fromWireEntity, type PullResponse, type PushResponse } from '@kemicontrol/domain';
import { useLiveQuery } from 'dexie-react-hooks';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthContext';
import { db, entityKey, type StoredConflict, type StoredEntity, type StoredSyncState, type SyncStatus } from '../data/db';
import { decodeStoredOperation, ensureRecurringExpenses, markMutationApplied } from '../data/mutations';

interface SyncContextValue {
  status: SyncStatus;
  pending: number;
  conflicts: number;
  state: StoredSyncState | undefined;
  syncNow(): Promise<void>;
  subscribePush(): Promise<void>;
}

const SyncContext = createContext<SyncContextValue | undefined>(undefined);

async function api<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, { ...init, headers: { 'content-type': 'application/json', authorization: `Bearer ${token}`, ...init?.headers } });
  if (!response.ok) throw new Error(`Servidor de sincronização: ${response.status}`);
  return await response.json() as T;
}

async function applyPull(userId: string, token: string): Promise<void> {
  let state = await db.syncState.get(userId) ?? { userId, lastRevision: 0, status: 'syncing' as const };
  let hasMore = true;
  while (hasMore) {
    const result = await api<PullResponse>(`/api/v1/sync/pull?afterRevision=${state.lastRevision}&limit=100`, token);
    await db.transaction('rw', db.entities, db.outbox, db.conflicts, db.syncState, async () => {
      for (const change of result.changes) {
        for (const operation of change.operations) {
          const pending = await db.outbox.where('userId').equals(userId).filter((group) => group.operations.some((candidate) => candidate.entityType === operation.entityType && candidate.entityId === operation.entityId)).first();
          const record = fromWireEntity(operation.entityType, operation.record);
          if (pending) {
            const localOperation = pending.operations.find((candidate) => candidate.entityType === operation.entityType && candidate.entityId === operation.entityId);
            const localRecord = localOperation ? decodeStoredOperation(localOperation) : undefined;
            if (localRecord && localRecord.version !== record.version) {
              const conflict: StoredConflict = { id: crypto.randomUUID(), userId, mutationId: pending.mutationId, entityType: operation.entityType, entityId: operation.entityId, localRecord, remoteRecord: record, createdAt: new Date().toISOString() };
              await db.conflicts.put(conflict);
              continue;
            }
          }
          const value: StoredEntity = { key: entityKey(userId, operation.entityType, operation.entityId), userId, type: operation.entityType, record };
          await db.entities.put(value);
        }
      }
      state = { userId, lastRevision: result.nextRevision, status: 'syncing', lastSyncAt: state.lastSyncAt };
      await db.syncState.put(state);
    });
    hasMore = result.hasMore;
  }
}

export function SyncProvider({ children }: { children: ReactNode }) {
  const { user, accessToken } = useAuth();
  const running = useRef(false);
  const state = useLiveQuery(() => user ? db.syncState.get(user.id) : undefined, [user?.id]);
  const pending = useLiveQuery(() => user ? db.outbox.where('userId').equals(user.id).count() : 0, [user?.id], 0);
  const conflicts = useLiveQuery(() => user ? db.conflicts.where('userId').equals(user.id).count() : 0, [user?.id], 0);

  const syncNow = useCallback(async () => {
    if (!user || running.current) return;
    running.current = true;
    try {
      const startingState = await db.syncState.get(user.id);
      if (user.local) {
        await ensureRecurringExpenses(user.id);
        await db.outbox.where('userId').equals(user.id).delete();
        await db.syncState.put({ userId: user.id, lastRevision: startingState?.lastRevision ?? 0, status: 'saved-local', lastSyncAt: new Date().toISOString() });
        return;
      }
      if (!navigator.onLine || user.offline) {
        await db.syncState.put({ userId: user.id, lastRevision: startingState?.lastRevision ?? 0, status: 'offline', lastSyncAt: startingState?.lastSyncAt });
        return;
      }
      const token = await accessToken();
      if (!token) return;
      await db.syncState.put({ userId: user.id, lastRevision: startingState?.lastRevision ?? 0, status: 'syncing', lastSyncAt: startingState?.lastSyncAt });
      await ensureRecurringExpenses(user.id);
      const blockedMutationIds = new Set((await db.conflicts.where('userId').equals(user.id).toArray()).map((conflict) => conflict.mutationId));
      const groups = (await db.outbox.where('userId').equals(user.id).sortBy('createdAt')).filter((group) => !blockedMutationIds.has(group.mutationId));
      for (let index = 0; index < groups.length; index += 10) {
        const batch = groups.slice(index, index + 10);
        const response = await api<PushResponse>('/api/v1/sync/push', token, { method: 'POST', body: JSON.stringify({ deviceId: getDeviceId(), mutationGroups: batch.map(({ mutationId, operations }) => ({ mutationId, operations })) }) });
        for (const result of response.results) {
          const group = batch.find((candidate) => candidate.mutationId === result.mutationId);
          if (!group) continue;
          if (result.status === 'conflict') {
            for (const conflict of result.conflicts) {
              const localOperation = group.operations.find((operation) => operation.entityType === conflict.entityType && operation.entityId === conflict.entityId);
              const value: StoredConflict = { id: crypto.randomUUID(), userId: user.id, mutationId: group.mutationId, entityType: conflict.entityType, entityId: conflict.entityId, localRecord: localOperation ? decodeStoredOperation(localOperation) : undefined, remoteRecord: fromWireEntity(conflict.entityType, conflict.record), createdAt: new Date().toISOString() };
              await db.conflicts.put(value);
            }
            continue;
          }
          await markMutationApplied(user.id, group, result.revision, result.operations);
        }
      }
      await applyPull(user.id, token);
      await ensureRecurringExpenses(user.id);
      const conflictCount = await db.conflicts.where('userId').equals(user.id).count();
      const pendingCount = await db.outbox.where('userId').equals(user.id).count();
      await db.syncState.put({ userId: user.id, lastRevision: (await db.syncState.get(user.id))?.lastRevision ?? 0, status: conflictCount ? 'conflict' : pendingCount ? 'saved-local' : 'synced', lastSyncAt: new Date().toISOString() });
    } catch (error) {
      const failedState = await db.syncState.get(user.id);
      await db.syncState.put({ userId: user.id, lastRevision: failedState?.lastRevision ?? 0, status: navigator.onLine ? 'error' : 'offline', lastSyncAt: failedState?.lastSyncAt, error: error instanceof Error ? error.message : 'Falha ao sincronizar.' });
    } finally {
      running.current = false;
    }
  }, [user, accessToken]);

  useEffect(() => {
    if (!user) return;
    void syncNow();
    const onOnline = () => void syncNow();
    const onFocus = () => { if (document.visibilityState === 'visible') void syncNow(); };
    window.addEventListener('online', onOnline);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') void syncNow(); }, 60_000);
    return () => { window.removeEventListener('online', onOnline); window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onFocus); window.clearInterval(timer); };
  }, [user, syncNow]);

  const subscribePush = useCallback(async () => {
    if (!user || user.local) throw new Error('Notificações remotas exigem a implantação AWS.');
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) throw new Error('Este navegador não oferece Web Push.');
    const token = await accessToken();
    if (!token) throw new Error('Entre novamente para ativar notificações.');
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') throw new Error('Permissão de notificações não concedida.');
    const { publicKey } = await api<{ publicKey: string }>('/api/v1/push/public-key', token);
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) });
    await api(`/api/v1/push-subscriptions/${encodeURIComponent(getDeviceId())}`, token, { method: 'PUT', body: JSON.stringify(subscription.toJSON()) });
  }, [user, accessToken]);

  const status: SyncStatus = conflicts ? 'conflict' : state?.status ?? (navigator.onLine ? 'saved-local' : 'offline');
  const value = useMemo(() => ({ status, pending, conflicts, state, syncNow, subscribePush }), [status, pending, conflicts, state, syncNow, subscribePush]);
  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
}

function getDeviceId(): string {
  const existing = localStorage.getItem('kemi.device-id');
  if (existing) return existing;
  const value = crypto.randomUUID();
  localStorage.setItem('kemi.device-id', value);
  return value;
}

function urlBase64ToUint8Array(value: string): ArrayBuffer {
  const padding = '='.repeat((4 - value.length % 4) % 4);
  const raw = atob((value + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (character) => character.charCodeAt(0)).buffer as ArrayBuffer;
}

export function useSync(): SyncContextValue {
  const value = useContext(SyncContext);
  if (!value) throw new Error('SyncProvider ausente.');
  return value;
}
